import {ActivityReview} from '@/containers';
import {ProgressIndicator, HeaderTitle, Breadcrumb} from '@/components/atoms';
import DuplicateAlert from '@/components/atoms/Toast/DuplicateAlert';
import {ROUTES} from '@/utils/constants/routesConstants';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import useActivityReview from '@/containers/Activity/ActivityReview/ActivityReview-hook';
import useSurveyActions from '@/containers/Activity/ActivityReview/hooks/useSurveyActions';
import {getModal} from '@/utils/CommonModal';
import {useAppSelector} from '@/store';
import {useMemo} from 'react';
import {Constants} from '@/utils/constants/commonConstants';
import {ACTIVITY_TYPE, isVideoActivityType} from '@/types/Activity';
import SurveyActionButtons from '@/containers/Activity/ActivityReview/components/SurveyActionButtons';

const AddActivityReview = () => {
  const navigate = useNavigate();
  const {id} = useParams();
  const [searchParams] = useSearchParams();
  const activityFormData = useAppSelector((state) => state.activity.formData);

  const reviewParam = searchParams.get('review');
  const isReviewCompleted =
    reviewParam?.toLowerCase() === Constants.ReviewCompleted.toLowerCase();
  const showProgressIndicator = !id;

  const {
    handleCancel,
    onClickPrimaryButton,
    onClickSecondaryButton,
    isCancel,
    handlePublish
  } = useActivityReview();

  // Dynamic breadcrumb logic
  const breadcrumbs = useMemo(() => {
    const baseBreadcrumbs = [
      {label: '', href: ROUTES.HOME},
      {label: 'Loyalty Campaigns', href: ROUTES.ACTIVITIES}
    ];

    // Check if we have an existing activity (either from URL id or data id/uuid)
    const hasExistingActivity =
      id || activityFormData?.uuid || activityFormData?.id;
    const activityName = activityFormData?.activityName;

    // Creation flow - no id in URL and no existing data
    if (!hasExistingActivity) {
      return [
        ...baseBreadcrumbs,
        {label: 'Add Activity', href: ROUTES.ACTIVITIES}
      ];
    }

    // Review/Edit flow - id exists in URL or data has id/uuid
    const displayName = activityName || 'Loading...';
    return [
      ...baseBreadcrumbs,
      {label: 'Activity', href: ROUTES.ACTIVITIES},
      {label: displayName, href: ROUTES.ACTIVITIES}
    ];
  }, [
    id,
    activityFormData?.activityName,
    activityFormData?.uuid,
    activityFormData?.id
  ]);

  const isSurveyFlow = activityFormData?.activityType === ACTIVITY_TYPE.SURVEY;
  const isVideoFlow = isVideoActivityType(activityFormData?.activityType);
  const isThreeStepFlow = isSurveyFlow || isVideoFlow;
  const isSurveyViewMode = isSurveyFlow && !!id;

  const surveyActions = useSurveyActions(
    isSurveyViewMode ? {handlePublish} : undefined
  );

  const pageTitle =
    isSurveyFlow && id
      ? activityFormData?.activityName || 'Survey Review'
      : 'Activity Review';

  const handleStepClick = (step) => {
    if (isSurveyFlow) {
      if (step === 1) navigate(ROUTES.ACTIVITY_ADD);
      if (step === 2) navigate(ROUTES.ACTIVITY_ATTRIBUTES);
      return;
    }
    if (isVideoFlow) {
      if (step === 1) navigate(ROUTES.ACTIVITY_ADD);
      if (step === 2) navigate(ROUTES.ACTIVITY_DISPLAY);
      return;
    }
    if (step === 1) {
      navigate(ROUTES.ACTIVITY_ADD);
    }
  };

  const getCancelModal = () => {
    return getModal({
      title: 'Are you sure you want to cancel?',
      text: 'Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO',
      showPrimaryButton: true,
      primaryButtonText: 'Yes',
      onClickPrimaryButton: onClickPrimaryButton,
      showSecondaryButton: true,
      secondaryButtonText: 'No',
      onClickSecondaryButton: onClickSecondaryButton,
      showModal: isCancel,
      onCloseModal: handleCancel,
      hideCloseButton: false
    });
  };

  return (
    <>
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: breadcrumbs
        }}
        id={'aar_bradcrumb'}
      />
      <HeaderTitle
        title={pageTitle}
        className='mb-3 justify-start text-3xl font-bold text-slate-200'
      />
      {isSurveyViewMode && surveyActions.actionAlert && (
        <DuplicateAlert
          type={surveyActions.actionAlert.type}
          message={{
            title: surveyActions.actionAlert.title,
            subTitle: surveyActions.actionAlert.subTitle
          }}
          show={true}
          onCloseClick={surveyActions.handleDismissAlert}
        />
      )}
      {isSurveyViewMode && (
        <div className='flex justify-end'>
          <SurveyActionButtons surveyActions={surveyActions} />
        </div>
      )}
      {showProgressIndicator && !isReviewCompleted && (
        <ProgressIndicator
          currentStep={isThreeStepFlow ? 3 : 2}
          completed={false}
          steps={isThreeStepFlow ? 3 : 2}
          titles={
            isSurveyFlow
              ? [{title: 'Setup'}, {title: 'Attributes'}, {title: 'Review'}]
              : isVideoFlow
                ? [{title: 'Setup'}, {title: 'Display'}, {title: 'Review'}]
                : [{title: 'Setup'}, {title: 'Review'}]
          }
          className='mb-6'
          onStepClick={handleStepClick}
        />
      )}
      <br />
      <ActivityReview
        cancelModalHandlers={{
          handleCancel,
          onClickPrimaryButton,
          onClickSecondaryButton,
          isCancel
        }}
        isSurveyViewMode={isSurveyViewMode}
      />
      {isCancel && getCancelModal()}
    </>
  );
};

export default AddActivityReview;
